import { fetchTab } from "@/api/hooks/tab";
import type { ElementStyle } from "@mm/metx-workbench.meteomatics.com";
import { type PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import type { RootState } from ".";

import { createLocationTable } from "@/api/hooks/tools/locationTable";
import { type LocationTable, transformToLocationTable } from "@/locationTable/types";
import Logger from "@mm/logging";
import { DateTime, Duration } from "luxon";

const logger = Logger.fromFilename(__filename);

export type LocationTableState = LocationTable & { exportCsvTrigger?: boolean; exportPdfTrigger?: boolean };

export type LocationTablesState = {
  [id: number]: LocationTableState;
};

const initialState: LocationTablesState = {};

const locationTableSlice = createSlice({
  name: "locationTable",
  initialState: initialState as LocationTablesState,
  reducers: {
    addLocationTable(state, action: PayloadAction<LocationTableState>) {
      state[action.payload.id] = action.payload;
    },
    discardLocationTable(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const tool = state[id];

      if (!tool) {
        logger.error("Could not find tool with id", id);
        return;
      }
      delete state[id];
    },
    setTableTitle(state, action: PayloadAction<{ id: number; title: string }>) {
      const { id, title } = action.payload;

      const locationTable = state[id];
      if (!locationTable) {
        logger.error("Could not find tool with id", id);
        return;
      }

      locationTable.title = title;
    },
    setTableTitleStyle(state, action: PayloadAction<{ style: ElementStyle; id: number }>) {
      const { id, style } = action.payload;

      const locationTable = state[id];
      if (!locationTable) {
        logger.error("Could not find tool with id", id);
        return;
      }

      locationTable.titleStyle = style;
    },
    setTableTimeRange(
      state,
      action: PayloadAction<{
        id: number;
        props: { datetimeRange: [string | null, string | null]; resolution: string; referenceTime: string };
      }>,
    ) {
      const {
        id,
        props: {
          datetimeRange: [from, to],
          resolution,
          referenceTime,
        },
      } = action.payload;

      const table = state[id];

      if (!table) {
        logger.error("Could not find table with id", id);
        return;
      }
      table.resolution = resolution;
      if (!table.timeLock) {
        table.datetimeFrom = null;
        table.datetimeTo = null;

        table.datetimeFromOffset = from ? DateTime.fromISO(from).diff(DateTime.fromISO(referenceTime)).toISO() : null;
        table.datetimeToOffset = to ? DateTime.fromISO(to).diff(DateTime.fromISO(referenceTime)).toISO() : null;

        return;
      }

      table.datetimeFrom = from;
      table.datetimeTo = to;
    },

    adjustAllLocationTableDateTimeRange(state, action: PayloadAction<{ durationMinutes: number }>) {
      const { durationMinutes } = action.payload;
      for (const tableId in state) {
        const table = state[tableId];
        const { datetimeFrom, datetimeTo } = table;
        if (datetimeFrom) {
          const newFrom = DateTime.fromISO(datetimeFrom).plus({ minute: durationMinutes });
          const newTo = datetimeTo ? DateTime.fromISO(datetimeTo).plus({ minute: durationMinutes }) : null;
          table.datetimeFrom = newFrom.toISO();
          table.datetimeTo = newTo?.toISO() || null;
        }
      }
    },

    updateLocationTableState(
      state,
      action: PayloadAction<{
        id: LocationTableState["id"];
        locationTable: Partial<LocationTableState>;
      }>,
    ) {
      const { locationTable, id } = action.payload;

      const table = state[id];

      if (!table) {
        logger.error("Could not find location table with id", id);
        return;
      }

      state[id] = {
        ...table,
        ...locationTable,
      };
    },
    pasteLocationTable(state, action: PayloadAction<{ id: number; locationTable: Partial<LocationTableState> }>) {
      const { id, locationTable } = action.payload;
      const table = state[id];

      if (!table) {
        logger.error("Could not find location table with id", id);
        return;
      }
      // removes id's from previous and title and titleStyle
      const { id: _locationTableId, tabId, title, titleStyle, ...newLocationTable } = locationTable;

      state[id] = { ...table, ...newLocationTable, id };
    },
    setLocationTableTimeSettings: (
      state,
      action: PayloadAction<{ id: number; timeLock: boolean; from: string; to: string; referenceTime: string }>,
    ) => {
      const { id, timeLock, from, to, referenceTime } = action.payload;
      const table = state[id];

      if (!table) {
        logger.error("Could not find table with id", id);
        return;
      }

      table.timeLock = timeLock;

      if (!timeLock) {
        table.datetimeFrom = null;
        table.datetimeFromOffset = from ? DateTime.fromISO(from).diff(DateTime.fromISO(referenceTime)).toISO() : null;

        table.datetimeTo = null;
        table.datetimeToOffset = to ? DateTime.fromISO(to).diff(DateTime.fromISO(referenceTime)).toISO() : null;

        return;
      }

      table.datetimeFrom = table.datetimeFromOffset
        ? DateTime.fromISO(referenceTime).plus(Duration.fromISO(table.datetimeFromOffset)).toISO()
        : DateTime.fromISO(from).toISO();
      table.datetimeFromOffset = null;

      table.datetimeTo = table.datetimeToOffset
        ? DateTime.fromISO(referenceTime).plus(Duration.fromISO(table.datetimeToOffset)).toISO()
        : DateTime.fromISO(to).toISO();
      table.datetimeToOffset = null;
    },
    triggerLocationTableExport(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const table = state[id];
      if (!table) {
        logger.error("Could not find table with id", id);
        return;
      }
      table.exportCsvTrigger = true;
    },

    resetLocationExportFlag(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const table = state[id];
      if (!table) {
        logger.error("Could not find table with id", id);
        return;
      }
      table.exportCsvTrigger = false;
    },
    triggerLocationTablePdfExport(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const table = state[id];
      if (!table) {
        logger.error("Could not find table with id", id);
        return;
      }
      table.exportPdfTrigger = true;
    },

    resetLocationTablePdfExportFlag(state, action: PayloadAction<{ id: number }>) {
      const { id } = action.payload;
      const table = state[id];
      if (!table) {
        logger.error("Could not find table with id", id);
        return;
      }
      table.exportPdfTrigger = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchTab.fulfilled, (state, action) => {
      const tab = action.payload;
      for (const key in state) {
        delete state[+key];
      }
      if (tab.location_tables) {
        for (const table of tab.location_tables) {
          state[table.id] = transformToLocationTable(table);
        }
      }
    });
    builder.addCase(createLocationTable.fulfilled, (state, action) => {
      const table = action.payload.tool;
      state[table.id] = transformToLocationTable(table);
    });
  },
});

const selectLocationTables = (state: RootState) => state.tabGroup.present.locationTables;

const makeSelectLocationTablesByTabId = createSelector(
  [selectLocationTables, (state: RootState, tabId: number) => tabId],
  (locationTables, tabId) => Object.values(locationTables).filter((table) => table.tabId === tabId),
);

export function useLocationTables(tabId: number) {
  return useSelector((state: RootState) => makeSelectLocationTablesByTabId(state, tabId));
}

const makeSelectLocationTableById = createSelector(
  [selectLocationTables, (state: RootState, id: number) => id],
  (locationTables, id) => locationTables[id],
);

export function useLocationTable(id: number) {
  return useSelector((state: RootState) => makeSelectLocationTableById(state, id));
}

export const {
  updateLocationTableState,
  discardLocationTable,
  setTableTitle,
  setTableTitleStyle,
  setTableTimeRange,
  setLocationTableTimeSettings,
  adjustAllLocationTableDateTimeRange,
  triggerLocationTableExport,
  resetLocationExportFlag,
  triggerLocationTablePdfExport,
  resetLocationTablePdfExportFlag,
  pasteLocationTable,
} = locationTableSlice.actions;

export default locationTableSlice.reducer;
